/**
 * Project-administration schemas (desktop Configuration console). Project-level operators
 * (holders of `can_manage_project`) administer the whole project: its language
 * catalog, teams, people, roles, and system defaults.
 *
 * A "user" here may be project-scoped (an `ProjectMembership` — project admins/observers) or
 * team-scoped (a `TeamMembership`). Both carry multiple `roleIds`; effective
 * permissions are the union. Passwords are write-only (argon2), never returned.
 */

import { z } from 'zod';
import { languoidCodeSchema } from './primitives.js';
import { membershipStatusSchema, type MembershipStatus } from './admin.js';

const passwordSchema = z.string().min(8, 'Password must be at least 8 characters.').max(200);
const raterModeSchema = z.enum(['SINGLE', 'MULTI']);
export type MemberScope = 'project' | 'team';

// --- Project ----------------------------------------------------------

export interface ProjectSummary {
  id: string;
  name: string;
  languoidCodes: string[];
  defaultGeminiQuota: number;
}

export const projectUpdateSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  languoidCodes: z.array(languoidCodeSchema).optional(),
  defaultGeminiQuota: z.number().int().min(0).optional(),
});
export type ProjectUpdateInput = z.infer<typeof projectUpdateSchema>;

// --- Languoid catalog ------------------------------------------------------

/** One entry in the live languoid catalog (DB-backed, extended at runtime). */
export interface LanguoidSummary {
  code: string;
  iso639_3: string;
  script: string;
  englishName: string;
  endonym: string | null;
}

/**
 * Define a new languoid mid-project. The `code` is derived server-side as
 * `${iso639_3}_${script}` (e.g. "hau_Latn"); the inputs are normalized here so
 * the desktop and server agree on casing.
 */
export const languoidCreateSchema = z.object({
  englishName: z.string().min(1, 'An English name is required.').max(120),
  iso639_3: z
    .string()
    .regex(/^[A-Za-z]{3}$/, 'ISO 639-3 is 3 letters (e.g. "hau").')
    .transform((s) => s.toLowerCase()),
  script: z
    .string()
    .regex(/^[A-Za-z]{4}$/, 'Script is a 4-letter ISO 15924 code (e.g. "Latn").')
    .transform((s) => s[0]!.toUpperCase() + s.slice(1).toLowerCase()),
  endonym: z.string().max(120).optional(),
});
export type LanguoidCreateInput = z.infer<typeof languoidCreateSchema>;
export type LanguoidCreateRequest = z.input<typeof languoidCreateSchema>;

// --- Teams -----------------------------------------------------------------

export interface ProjectTeamSummary {
  id: string;
  name: string;
  languoidCodes: string[];
  raterMode: 'SINGLE' | 'MULTI';
  onboardingReviewThreshold: number;
  /** Display name of a member whose role grants `can_manage_team`, if any. */
  leadName: string | null;
  memberCount: number;
  challengeCount: number;
}

export const teamCreateSchema = z.object({
  name: z.string().min(1, 'A team name is required.').max(160),
  languoidCodes: z.array(languoidCodeSchema).min(1, 'Assign at least one language.'),
  raterMode: raterModeSchema.default('SINGLE'),
  onboardingReviewThreshold: z.number().int().min(0).default(10),
});
export type TeamCreateInput = z.infer<typeof teamCreateSchema>;
export type TeamCreateRequest = z.input<typeof teamCreateSchema>;

export const teamUpdateSchema = teamCreateSchema.partial();
export type TeamUpdateInput = z.infer<typeof teamUpdateSchema>;

// --- Segment purge (Configuration danger zone) ------------------------------

/** A team segment shown in the danger-zone list, for selective deletion. */
export interface TeamSegmentSummary {
  id: string;
  segmentId: string;
  languoidCode: string;
  domain: string;
  state: string;
}

/** Delete selected segments (by challenge id), optionally clearing their prep-sheet rows. */
export const segmentDeleteSchema = z.object({
  challengeIds: z.array(z.string().min(1)).min(1, 'Select at least one segment.'),
  clearSheet: z.boolean().default(false),
});
export type SegmentDeleteInput = z.infer<typeof segmentDeleteSchema>;
export type SegmentDeleteRequest = z.input<typeof segmentDeleteSchema>;

/** Clear ALL of a team's segments (fresh start), optionally clearing the prep-sheet rows. */
export const segmentClearSchema = z.object({
  clearSheet: z.boolean().default(false),
});
export type SegmentClearInput = z.infer<typeof segmentClearSchema>;
export type SegmentClearRequest = z.input<typeof segmentClearSchema>;

/** What a purge removed (hard-deleted challenges + prep-sheet rows cleared). */
export interface SegmentPurgeResult {
  deleted: number;
  sheetRowsCleared: number;
}

// --- Users (project-wide) ------------------------------------------------------

export interface ProjectUserSummary {
  userId: string;
  displayName: string | null;
  email: string;
  scope: MemberScope;
  teamId: string | null;
  teamName: string | null;
  roleIds: string[];
  roleNames: string[];
  permissions: string[];
  /** Union of the roles' platforms. */
  platforms: string[];
  languoidCodes: string[];
  status: MembershipStatus;
}

export const projectUserCreateSchema = z
  .object({
    displayName: z.string().min(1, 'A name is required.').max(120),
    email: z.string().email().transform((s) => s.trim().toLowerCase()),
    password: passwordSchema,
    scope: z.enum(['project', 'team']),
    teamId: z.string().optional(),
    roleIds: z.array(z.string()).min(1, 'Assign at least one role.'),
    languoidCodes: z.array(languoidCodeSchema).default([]),
    customPermissions: z.array(z.string()).default([]),
    status: membershipStatusSchema.default('ACTIVE'),
  })
  .refine((d) => d.scope === 'project' || !!d.teamId, {
    message: 'A team is required for a team-scoped user.',
    path: ['teamId'],
  });
export type ProjectUserCreateInput = z.infer<typeof projectUserCreateSchema>;
export type ProjectUserCreateRequest = z.input<typeof projectUserCreateSchema>;

export const projectUserUpdateSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  password: passwordSchema.optional(),
  roleIds: z.array(z.string()).min(1).optional(),
  languoidCodes: z.array(languoidCodeSchema).optional(),
  customPermissions: z.array(z.string()).optional(),
  status: membershipStatusSchema.optional(),
});
export type ProjectUserUpdateInput = z.infer<typeof projectUserUpdateSchema>;

// --- Roles -----------------------------------------------------------------

export interface ProjectRoleSummary {
  id: string;
  name: string;
  description: string | null;
  scope: MemberScope;
  teamId: string | null;
  platforms: string[];
  permissions: string[];
  isSystemRole: boolean;
}

export const roleCreateSchema = z
  .object({
    name: z.string().min(1, 'A role name is required.').max(120),
    description: z.string().max(2000).optional(),
    scope: z.enum(['project', 'team']),
    teamId: z.string().optional(),
    platforms: z.array(z.string()).default([]),
    permissions: z.array(z.string()).default([]),
  })
  .refine((d) => d.scope === 'project' || !!d.teamId, {
    message: 'A team is required for a team-scoped role.',
    path: ['teamId'],
  });
export type RoleCreateInput = z.infer<typeof roleCreateSchema>;
export type RoleCreateRequest = z.input<typeof roleCreateSchema>;

export const roleUpdateSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).optional(),
  platforms: z.array(z.string()).optional(),
  permissions: z.array(z.string()).optional(),
});
export type RoleUpdateInput = z.infer<typeof roleUpdateSchema>;

// --- System ----------------------------------------------------------------

export interface ProjectSystemInfo {
  defaultGeminiQuota: number;
  /** Project-wide default Google-sync cadence (minutes); teams may override per team. */
  defaultSyncCadenceMinutes: number;
  /** Google Sheet the export mirror writes each shipped dataset into (one tab per language);
   *  null = the mirror is off. The service account must have edit access to it. */
  masterSpreadsheetId: string | null;
  /** Shared Google Drive folder of English SEGMENT audio for reverse challenges — the same clips
   *  for every language (docs/reverse-challenges-plan.md §4). Null = reverse segment audio off. */
  reverseSegmentFolderId: string | null;
  /** Aggregate monthly token usage across the project's teams. */
  usedTokens: number;
  limitTokens: number;
  /** READ-ONLY — the locked model (§1.2 invariant); not configurable. */
  geminiModel: string;
  /** Whether a Gemini API key is configured on the server (env). */
  geminiKeyConfigured: boolean;
}

export const systemUpdateSchema = z.object({
  defaultGeminiQuota: z.number().int().min(0).optional(),
  /** Project default sync cadence in minutes (5 min–24 h). */
  defaultSyncCadenceMinutes: z.number().int().min(5).max(1440).optional(),
  /** The export mirror's master spreadsheet id; an empty string clears it (mirror off). */
  masterSpreadsheetId: z.string().max(200).optional(),
  /** Shared reverse-challenge English SEGMENT audio folder id; an empty string clears it. */
  reverseSegmentFolderId: z.string().max(200).optional(),
});
export type SystemUpdateInput = z.infer<typeof systemUpdateSchema>;
